import userImg from '../../../../assets/user.png';
import linkedin from '../../../../assets/linkedin.svg';
import CalendarIcon from '../../../../assets/Svg/CalendarIcon';
import { userBlack } from '../../../../assets/hostedassets';
import { isSafeUrl, toTitleCase } from '../../../../utils/utils';
import { DATE_FORMATS, formatAgeValue, formatDateByFormat, formatTenureYears } from '../../../../utils/dateUtils';
import type { DirectorTableRow } from './useManagementProfileTabLogic';
import styles from './MobileDirectorCard.module.scss';

type Props = {
    row: DirectorTableRow;
    onClick: (row: DirectorTableRow) => void;
};

/**
 * One director as a card — the mobile stand-in for a DataTable row (Figma node 3744-84138).
 *
 * Deliberately built from the same pieces the desktop cells use (userImg + its onError
 * fallback, the linkedin mark behind an isSafeUrl guard, CalendarIcon, toTitleCase,
 * formatTenureYears/formatAgeValue) so the two surfaces can't show a director differently.
 * Every field is hide-when-absent, matching the drawer's behaviour from RECUR-27244.
 * RECUR-27903.
 */
const MobileDirectorCard = ({ row, onClick }: Props) => {
    const age = formatAgeValue(row.age);
    const dob = row.dob ? formatDateByFormat(row.dob, DATE_FORMATS.DATE_FORMAT_DDMMYYYY) : undefined;
    const tenureYears = row.tenureBeginDate ? formatTenureYears(row.tenureBeginDate, row.tenureEndDate) : undefined;
    const tenureRange = row.tenureBeginDate
        ? `(${formatDateByFormat(row.tenureBeginDate, DATE_FORMATS.MONTH_SHORT_YEAR)} - ${
              row.tenureEndDate ? formatDateByFormat(row.tenureEndDate, DATE_FORMATS.MONTH_SHORT_YEAR) : row.isActive ? 'Present' : ''
          })`
        : undefined;

    return (
        <div className={styles.Card} onClick={() => onClick(row)}>
            <div className={styles.TopRow}>
                <div className={styles.Avatar}>
                    <img
                        src={row.imageUrl || userImg}
                        alt={row.name}
                        onError={({ currentTarget }) => {
                            currentTarget.onerror = null;
                            currentTarget.src = userImg;
                        }}
                    />
                </div>

                <div className={styles.Identity}>
                    <button
                        type="button"
                        className={styles.Name}
                        onClick={(e) => {
                            e.stopPropagation();
                            onClick(row);
                        }}
                    >
                        {toTitleCase(row.name)}
                    </button>
                    {(dob || row.gender) && (
                        <div className={styles.Pills}>
                            {dob && (
                                <span className={styles.Pill}>
                                    <CalendarIcon className={styles.PillIcon} />
                                    <span className={styles.PillLabel}>DOB:</span>
                                    <span className={styles.PillValue}>
                                        {dob}
                                        {age ? ` (${age})` : ''}
                                    </span>
                                </span>
                            )}
                            {row.gender && (
                                <span className={styles.Pill}>
                                    <img src={userBlack} alt="" width="12" height="12" />
                                    <span className={styles.PillValue}>{toTitleCase(row.gender)}</span>
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {row.linkedinUrl && isSafeUrl(row.linkedinUrl) && (
                    <a
                        href={row.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.LinkedIn}
                        aria-label={`${row.name} on LinkedIn`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img src={linkedin} alt="" />
                    </a>
                )}
            </div>

            {(row.designation || tenureYears) && (
                <div className={styles.Details}>
                    {row.designation && (
                        <div className={styles.DetailRow}>
                            <span className={styles.DetailLabel}>Directorship</span>
                            <div className={styles.DetailValue}>
                                <span>{row.designation}</span>
                                {row.isActive !== undefined && (
                                    <span className={row.isActive ? styles.StatusActive : styles.StatusInactive}>
                                        {row.isActive ? 'Active' : 'Former'}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                    {tenureYears && (
                        <div className={styles.DetailRow}>
                            <span className={styles.DetailLabel}>Tenure</span>
                            <div className={styles.DetailValue}>
                                <span>
                                    {tenureYears}
                                    <span className={styles.TenureRange}>{tenureRange}</span>
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MobileDirectorCard;
